/**
 * QQ bot management helpers (v0.5.0): daemon `/qq/*` wire parsing + the pure
 * decisions behind the 「管理QQ会话」 card.
 *
 * Everything returned by the daemon is untrusted and re-validated here (ids,
 * modes, titles, timestamps and the group/owner deny rules). Parsing never
 * throws: an invalid envelope returns `null` so the client surfaces a real
 * error instead of rendering a partly-fake status.
 *
 * Permission rules (DECISIONS.md IM-1, docs/v0.5.0-QQ-BOT.md):
 * - `chat` is the default and the only allowed mode for group chats;
 * - only the configured owner's private chat may use `approve`/`auto`;
 * - `auto` additionally needs an explicit danger confirmation (+ PIN when set).
 */

export type QqChatKind = 'private' | 'group';
export type QqMode = 'chat' | 'approve' | 'auto';

export interface QqChat {
  /** Canonical `${kind}:${id}` key (derived, never trusted verbatim). */
  key: string;
  kind: QqChatKind;
  id: string;
  title?: string;
  mode: QqMode;
  /** Seconds; daemon permission rows use epoch ms and are normalized here. */
  updated_at: number;
}

export interface QqStatus {
  enabled: boolean;
  connected: boolean;
  owner_id?: string;
  chats: QqChat[];
}

export interface QqModePatch {
  kind: QqChatKind;
  id: string;
  mode: QqMode;
  title?: string;
}

export interface QqPermissionsPatch {
  enabled?: boolean;
  owner_id?: string;
  mode?: QqModePatch;
}

/** Why the card can/cannot reach the daemon `/qq/*` endpoints right now. */
export interface QqAvailability {
  available: boolean;
  reason?: string;
}

export const QQ_MODES: readonly QqMode[] = ['chat', 'approve', 'auto'];

export const QQ_MODE_LABELS: Record<QqMode, string> = {
  chat: '聊天',
  approve: '审批',
  auto: '自动',
};

export const QQ_MODE_DESCRIPTIONS: Record<QqMode, string> = {
  chat: '无工具，只聊天（默认）',
  approve: '全工具；写操作仍需手机审批',
  auto: '全工具；需在 QQ 私聊发送“确认开启 D 档”后自动执行，直到“确认关闭 D 档”',
};

/**
 * v0.5.0-dev.2 fail-closed: the backend stores `auto` but executes it exactly
 * like `approve` (the gateway auto-bypass awaits a human-reviewed change) and
 * appends a fixed notice to the QQ reply. The card must never imply auto works:
 * it is labelled as inactive, and only a row the daemon already reports as
 * `auto` keeps the option selectable (so the displayed status stays truthful).
 */
export const QQ_AUTO_MODE_LABEL = '自动（需QQ确认）';
export const QQ_AUTO_MODE_HINT =
  'auto 档需在 QQ 私聊发送“确认开启 D 档”才会自动执行；关闭发“确认关闭 D 档”。';
export const QQ_AUTO_MODE_STORED_HINT = QQ_AUTO_MODE_HINT;

export const QQ_CHAT_KIND_LABELS: Record<QqChatKind, string> = {
  private: '私聊',
  group: '群聊',
};

export const QQ_TITLE_MAX_CHARS = 80;

/** QQ numbers are 5–12 digits (matches the daemon permission store). */
const QQ_ID_PATTERN = /^\d{5,12}$/u;
/** Values above this are epoch milliseconds rather than epoch seconds. */
const QQ_MS_THRESHOLD = 1e11;

export function qqChatKey(kind: QqChatKind, id: string): string {
  return `${kind}:${id}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isQqMode(value: unknown): value is QqMode {
  return value === 'chat' || value === 'approve' || value === 'auto';
}

function isQqChatKind(value: unknown): value is QqChatKind {
  return value === 'private' || value === 'group';
}

function normalizedTimestamp(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value > QQ_MS_THRESHOLD ? value / 1000 : value);
}

function normalizedTitle(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return Array.from(trimmed).slice(0, QQ_TITLE_MAX_CHARS).join('');
}

/** Parse one untrusted chat row; invalid rows are dropped by the caller. */
function qqChatFromUnknown(value: unknown): QqChat | null {
  if (!isRecord(value)) return null;
  const kind = value.kind;
  const id = value.id;
  if (!isQqChatKind(kind) || typeof id !== 'string' || !QQ_ID_PATTERN.test(id)) return null;
  // Fail safe: group chats are always `chat`, and an unknown mode also degrades
  // to the least-privileged `chat` instead of trusting the wire value.
  const mode = kind === 'group' || !isQqMode(value.mode) ? 'chat' : value.mode;
  const title = normalizedTitle(value.title);
  return {
    key: qqChatKey(kind, id),
    kind,
    id,
    mode,
    updated_at: normalizedTimestamp(value.updated_at),
    ...(title === undefined ? {} : { title }),
  };
}

/**
 * Parse `GET /qq/status` (also used for the `POST /qq/permissions` response).
 * Returns `null` when the envelope itself is invalid; individual malformed
 * chat rows are dropped and duplicates collapse (last row wins).
 */
export function qqStatusFromUnknown(value: unknown): QqStatus | null {
  if (!isRecord(value)) return null;
  if (typeof value.enabled !== 'boolean' || typeof value.connected !== 'boolean') return null;
  if (!Array.isArray(value.chats)) return null;
  const chats = new Map<string, QqChat>();
  for (const raw of value.chats) {
    const chat = qqChatFromUnknown(raw);
    if (chat !== null) chats.set(chat.key, chat);
  }
  const ownerId =
    typeof value.owner_id === 'string' && QQ_ID_PATTERN.test(value.owner_id)
      ? value.owner_id
      : undefined;
  return {
    enabled: value.enabled,
    connected: value.connected,
    ...(ownerId === undefined ? {} : { owner_id: ownerId }),
    chats: [...chats.values()],
  };
}

export type QqConnectionTone = 'success' | 'warning' | 'muted';

export interface QqConnectionView {
  label: string;
  tone: QqConnectionTone;
}

/** 「未启用 / 已连接 / 未连接」 for the card header and status row. */
export function qqConnectionState(status: QqStatus | null): QqConnectionView {
  if (status === null) return { label: '未加载', tone: 'muted' };
  if (!status.enabled) return { label: '未启用', tone: 'muted' };
  return status.connected
    ? { label: '已连接', tone: 'success' }
    : { label: '未连接', tone: 'warning' };
}

export function qqOwnerLabel(status: QqStatus | null): string {
  return status?.owner_id === undefined ? '未设置' : `QQ ${status.owner_id}`;
}

export function qqChatTitle(chat: QqChat): string {
  return chat.title ?? `${QQ_CHAT_KIND_LABELS[chat.kind]} ${chat.id}`;
}

export function qqChatIdLabel(chat: QqChat): string {
  return `${QQ_CHAT_KIND_LABELS[chat.kind]} · ${chat.id}`;
}

function qqChatIsOwner(status: QqStatus | null, chat: QqChat): boolean {
  return chat.kind === 'private' && status?.owner_id !== undefined && status.owner_id === chat.id;
}

/**
 * Effective displayed mode after the deny rules: group chats are forced to
 * `chat`, and a private chat that is not the configured owner also stays chat
 * (even if a stored row claims otherwise).
 */
export function qqDisplayMode(status: QqStatus | null, chat: QqChat): QqMode {
  if (!qqChatIsOwner(status, chat)) return 'chat';
  return chat.mode;
}

export function qqChatAllowsMode(status: QqStatus | null, chat: QqChat, mode: QqMode): boolean {
  if (mode === 'chat') return true;
  return qqChatIsOwner(status, chat);
}

/** Short UI hint when approve/auto are disabled for a row, else `null`. */
export function qqModeRestrictionHint(status: QqStatus | null, chat: QqChat): string | null {
  if (chat.kind === 'group') return '群聊永远聊天档，审批/自动不可选';
  if (!qqChatIsOwner(status, chat)) {
    return status?.owner_id === undefined
      ? '尚未设置 owner QQ，私聊只能聊天'
      : '只有 owner 的私聊可切换审批/自动';
  }
  return null;
}

/** Selector label: `auto` is visibly marked as not active yet (fail-closed). */
export function qqModeLabel(mode: QqMode): string {
  return mode === 'auto' ? QQ_AUTO_MODE_LABEL : QQ_MODE_LABELS[mode];
}

/**
 * Mode the QQ bot actually executes now. A stored `auto` row is displayed as
 * auto (so the user sees the stored setting) but executes as `approve`.
 */
export function qqEffectiveToolMode(status: QqStatus | null, chat: QqChat): QqMode {
  return qqDisplayMode(status, chat);
}

/**
 * Whether the mode selector may pick this mode. Owner/group deny rules apply
 * as before, and `auto` is fail-closed: it stays selectable only where the
 * daemon already reports it (selected-but-inactive), never as a new choice.
 */
export function qqModeOptionEnabled(status: QqStatus | null, chat: QqChat, mode: QqMode): boolean {
  return qqChatAllowsMode(status, chat, mode);
}

/**
 * Inline row hint: group/non-owner restrictions first, then the fail-closed
 * auto notice for owner private chats (including rows already stored as auto).
 */
export function qqModeRowHint(status: QqStatus | null, chat: QqChat): string | null {
  const restriction = qqModeRestrictionHint(status, chat);
  if (restriction !== null) return restriction;
  return qqDisplayMode(status, chat) === 'auto' ? QQ_AUTO_MODE_STORED_HINT : QQ_AUTO_MODE_HINT;
}

/** Tooltip for a disabled selector option (auto always explains fail-closed). */
export function qqModeDisabledHint(status: QqStatus | null, chat: QqChat, mode: QqMode): string {
  if (chat.kind === 'group') return '群聊永远聊天档，审批/自动不可选';
  if (mode === 'auto') return QQ_AUTO_MODE_HINT;
  return qqModeRestrictionHint(status, chat) ?? '当前不可选';
}

/** `auto` is the only mode that always needs an explicit danger confirmation. */
export function qqModeChangeRequiresConfirm(chat: QqChat, next: QqMode): boolean {
  return chat.kind === 'private' && next === 'auto';
}

/** Approve/auto go through the app PIN helper (no-op prompt when no PIN set). */
export function qqModeChangeRequiresPin(next: QqMode): boolean {
  return next !== 'chat';
}

/**
 * Build the `POST /qq/permissions` patch for a mode change, or `null` when the
 * change is not allowed / is already the effective mode. Group rows can only
 * ever produce `chat`.
 */
export function qqModePatch(
  status: QqStatus | null,
  chat: QqChat,
  next: QqMode,
): QqPermissionsPatch | null {
  if (!qqChatAllowsMode(status, chat, next)) return null;
  if (qqDisplayMode(status, chat) === next) return null;
  return {
    mode: {
      kind: chat.kind,
      id: chat.id,
      mode: next,
      ...(chat.title === undefined ? {} : { title: chat.title }),
    },
  };
}

/** `POST /qq/permissions` body: only the fields the user actually changed. */
export function qqPermissionsBody(patch: QqPermissionsPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.enabled !== undefined) body.enabled = patch.enabled;
  if (patch.owner_id !== undefined) body.owner_id = patch.owner_id;
  if (patch.mode !== undefined) {
    body.mode = {
      kind: patch.mode.kind,
      id: patch.mode.id,
      mode: patch.mode.mode,
      ...(patch.mode.title === undefined ? {} : { title: patch.mode.title }),
    };
  }
  return body;
}

/**
 * Apply a permissions patch to a local status snapshot. Used by the demo mock
 * client only: the real card always replaces its status with the daemon
 * response, so a failed call can never look like a successful write.
 *
 * Callers are expected to have validated approve/auto against the owner; group
 * rows are forced back to `chat` defensively.
 */
export function qqApplyPermissionsPatch(
  status: QqStatus,
  patch: QqPermissionsPatch,
  nowSeconds: number,
): QqStatus {
  const next: QqStatus = {
    enabled: patch.enabled ?? status.enabled,
    connected: status.connected,
    chats: status.chats.map((chat) => ({ ...chat })),
    ...(status.owner_id === undefined ? {} : { owner_id: status.owner_id }),
  };
  if (patch.owner_id !== undefined) next.owner_id = patch.owner_id;
  if (patch.mode === undefined) return next;
  const key = qqChatKey(patch.mode.kind, patch.mode.id);
  const index = next.chats.findIndex((chat) => chat.key === key);
  const existing = index >= 0 ? next.chats[index] : undefined;
  const title = normalizedTitle(patch.mode.title) ?? existing?.title;
  const row: QqChat = {
    key,
    kind: patch.mode.kind,
    id: patch.mode.id,
    mode: patch.mode.kind === 'group' ? 'chat' : patch.mode.mode,
    updated_at: Math.floor(nowSeconds),
    ...(title === undefined ? {} : { title }),
  };
  if (index >= 0) next.chats[index] = row;
  else next.chats.push(row);
  return next;
}

/**
 * QQ management transport availability.
 *
 * The daemon `/qq/*` endpoints are loopback HTTP APIs (device-auth, like the
 * other daemon APIs). The PWA's live path is the E2E relay, which does not
 * carry them yet, so live mode reports the exact missing bridge instead of
 * pretending the feature is connected. Demo mode uses the local mock client.
 */
export function qqDaemonAvailability(input: {
  demoMode: boolean;
  hasClient: boolean;
}): QqAvailability {
  if (input.hasClient) return { available: true };
  if (input.demoMode) {
    return { available: false, reason: '演示模式尚未初始化本地设备（请先完成配对）' };
  }
  return {
    available: false,
    reason:
      '手机端尚未接入 daemon HTTP 直连：/qq/* 管理接口目前只能由 daemon 本机或桌面端调用（TODO：待中继桥接）',
  };
}
